import { Fragment } from "react";
import { Link, useNavigate, NavigateFunction } from "react-router-dom";
import { formatDate } from "../../../helper/dateFormatter";
import {
  getModuleNextDueDate,
  getScoreTrend,
} from "../../Assessment-v2/utils.assessment";
import { VaccinationStatusItem } from "../../../api-helpers/ClinicalStatsTypes";

export interface CurrentModuleItem {
  assessmentId: string;
  formId?: string;
  assessment: string;
  score: number | string;
  lastCompletionDate: string | number | Date;
  previousScore?: number | string;
}

export interface TaskItem {
  taskId: string;
  taskSubtype: string;
  dueAt: string | Date;
}

interface NeedsAttentionBandProps {
  patientId: string;
  currentModules: CurrentModuleItem[];
  tasks: TaskItem[];
  openTaskCount: number;
  vaccinations: VaccinationStatusItem[];
}

// Rows shown per section - more overflow into "View all" rather than
// growing the card (design_handoff needs-attention-option-c, suggested).
const ROW_LIMIT = 4;

const isOverdueDate = (date: string | number | Date) =>
  new Date(date).getTime() < Date.now();

// Shared between AssessmentRailRow/AssessmentCompactRow, which otherwise
// only differ in their JSX layout.
interface AssessmentRowProps {
  item: CurrentModuleItem;
  patientId: string;
  navigate: NavigateFunction;
}

const getAssessmentRowState = (
  item: CurrentModuleItem,
  patientId: string,
  navigate: NavigateFunction
) => {
  const trend = getScoreTrend(item.assessment, item.score, item.previousScore);
  return {
    showArrow: trend !== null && trend !== "initial",
    hasScore:
      item.score !== undefined && item.score !== null && item.score !== "",
    goToAssessments: () => navigate(`/patients/${patientId}/assessments`),
  };
};

// 14x14 trend-arrow glyph. A single style/color for now -- direction
// variants are an open handoff question (design_handoff_needs_attention_all_overdue).
const TrendArrow = () => (
  <svg
    className="na-trend-arrow"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-label="Trend available"
    role="img"
  >
    <path
      d="M2.5 9.5L9.5 2.5M9.5 2.5H4.5M9.5 2.5V7.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SectionHeader = ({
  label,
  qualifier,
  viewAllTo,
}: {
  label: string;
  qualifier?: string;
  viewAllTo?: string;
}) => (
  <div className="na-section__header">
    <span className="ps-eyebrow">
      {label}
      {qualifier ? ` · ${qualifier}` : ""}
    </span>
    {viewAllTo && (
      <Link className="na-view-all" to={viewAllTo}>
        View all
      </Link>
    )}
  </div>
);

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M2.5 7.5L5.5 10.5L11.5 3.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HeaderBand = ({ attentionCount }: { attentionCount: number }) => (
  <div className="ps-needs-attention__header">
    <span
      className="ps-needs-attention__count"
      aria-label={`${attentionCount} items need attention`}
    >
      {attentionCount}
    </span>
    <div className="ps-needs-attention__subtitle">
      Overdue or missing items
    </div>
  </div>
);

// One section (Assessments/Tasks/Vaccinations): header + its rows, or a
// muted empty-state row when there's nothing to show. Shared by both rail
// and compact modes, each of which otherwise repeated this same
// header/list/empty-fallback shape three times.
function AttentionSection<T>({
  label,
  qualifier,
  viewAllTo,
  items,
  getKey,
  renderRow,
  emptyText,
  variant,
}: Readonly<{
  label: string;
  qualifier: string;
  viewAllTo?: string;
  items: T[];
  getKey: (item: T) => string;
  renderRow: (item: T) => React.ReactNode;
  emptyText: string;
  variant: "rail" | "compact";
}>) {
  return (
    <div className="na-section">
      <SectionHeader label={label} qualifier={qualifier} viewAllTo={viewAllTo} />
      {items.length > 0 ? (
        items.map((item) => (
          <Fragment key={getKey(item)}>{renderRow(item)}</Fragment>
        ))
      ) : (
        <EmptyRow text={emptyText} variant={variant} />
      )}
    </div>
  );
}

const EmptyRow = ({
  text,
  variant,
}: {
  text: string;
  variant: "rail" | "compact";
}) => (
  <div className={`na-row na-row--empty na-row--${variant}`}>
    <span className="na-row__empty-text">{text}</span>
  </div>
);

// ---- Rail mode (>= 1600px): single-line rows, Score/Due as separate cells --

const AssessmentRailRow = ({ item, patientId, navigate }: AssessmentRowProps) => {
  const { showArrow, hasScore, goToAssessments } = getAssessmentRowState(
    item,
    patientId,
    navigate
  );

  return (
    <button
      type="button"
      className="na-row na-row--rail na-row--rail-assessments na-row--interactive"
      onClick={goToAssessments}
    >
      <span className="na-row__name" title={item.assessment}>
        {item.assessment}
      </span>
      <span className="na-row__score">
        {hasScore ? item.score : "–"}
        {showArrow && <TrendArrow />}
      </span>
      <span className="na-row__due na-row__due--attention">
        {formatDate(getModuleNextDueDate(item.lastCompletionDate))}
      </span>
    </button>
  );
};

const TaskRailRow = ({ task }: { task: TaskItem }) => (
  <div className="na-row na-row--rail na-row--rail-tasks">
    <span className="na-row__name" title={task.taskSubtype}>
      {task.taskSubtype}
    </span>
    <span
      className={`na-row__due${
        isOverdueDate(task.dueAt) ? " na-row__due--attention" : ""
      }`}
    >
      {formatDate(task.dueAt)}
    </span>
  </div>
);

const VaccinationRailRow = ({ item }: { item: VaccinationStatusItem }) => (
  <div className="na-row na-row--rail na-row--rail-vaccinations">
    <span className="na-row__name" title={item.vaccine}>
      {item.vaccine}
    </span>
    <span className={`na-row__status na-row__status--${item.status}`}>
      {item.status === "needs-follow-up" ? "Needs follow-up" : "Missing"}
    </span>
  </div>
);

// ---- Compact mode (< 1600px): two-line rows, fixed 48px height -------------

const AssessmentCompactRow = ({
  item,
  patientId,
  navigate,
}: AssessmentRowProps) => {
  const { showArrow, hasScore, goToAssessments } = getAssessmentRowState(
    item,
    patientId,
    navigate
  );

  return (
    <button
      type="button"
      className="na-row na-row--compact na-row--interactive"
      onClick={goToAssessments}
    >
      <span className="na-row__name" title={item.assessment}>
        {item.assessment}
      </span>
      <span className="na-row__detail">
        Score <strong>{hasScore ? item.score : "–"}</strong>
        {showArrow && <TrendArrow />} ·{" "}
        <span className="na-row__due--attention">
          Due {formatDate(getModuleNextDueDate(item.lastCompletionDate))}
        </span>
      </span>
    </button>
  );
};

const TaskCompactRow = ({ task }: { task: TaskItem }) => (
  <div className="na-row na-row--compact">
    <span className="na-row__name" title={task.taskSubtype}>
      {task.taskSubtype}
    </span>
    <span
      className={`na-row__detail${
        isOverdueDate(task.dueAt) ? " na-row__due--attention" : ""
      }`}
    >
      Due {formatDate(task.dueAt)}
    </span>
  </div>
);

const VaccinationCompactRow = ({ item }: { item: VaccinationStatusItem }) => (
  <div className="na-row na-row--compact">
    <span className="na-row__name" title={item.vaccine}>
      {item.vaccine}
    </span>
    <span className={`na-row__detail na-row__status--${item.status}`}>
      {item.status === "needs-follow-up" ? "Needs follow-up" : "Missing"}
    </span>
  </div>
);

// "Needs attention" band (design_handoff_needs-attention-option-c): a 480px
// side rail on wide screens (>= 1600px) with Respiratory status/Care record
// filling the rest of the row, collapsing to a full-width 3-column card
// above them below that. The two modes' rows are structurally different
// (single-line 3-cell rows vs. two-line stacked text), not just a CSS
// reflow of the same markup, so both are rendered and toggled with
// `display: none` at the same 1600px breakpoint the page grid in
// Summary.tsx switches at - see .na-rail/.na-compact. This also gives each
// mode its own real semantic element (aside vs. section) for free, since a
// display:none subtree is dropped from the accessibility tree entirely.
export const NeedsAttentionBand = ({
  patientId,
  currentModules,
  tasks,
  openTaskCount,
  vaccinations,
}: NeedsAttentionBandProps) => {
  const navigate = useNavigate();
  const vaccinationsOverdue = vaccinations.filter(
    (v) => v.status === "missing" || v.status === "needs-follow-up"
  );

  const assessmentsCount = currentModules.length;
  const tasksCount = openTaskCount;
  const vaccinationsCount = vaccinationsOverdue.length;
  const attentionCount = assessmentsCount + tasksCount + vaccinationsCount;
  const hasAnything = attentionCount > 0;
  const titleId = `needs-attention-title-${patientId}`;

  const assessmentRows = currentModules.slice(0, ROW_LIMIT);
  const taskRows = tasks.slice(0, ROW_LIMIT);
  const vaccinationRows = vaccinationsOverdue.slice(0, ROW_LIMIT);

  // No overdue assessments, open tasks, or missing vaccinations: show a
  // positive confirmation rather than silently dropping the whole band,
  // which is indistinguishable from a failed/loading data fetch (see
  // Summary.tsx's fetchTasks/fetchAssessments and useClinicalStats, none of
  // which surface a fetch error here). One card works at any width, unlike
  // the rail/compact rows below, which need the real data to lay out.
  if (!hasAnything) {
    return (
      <div>
        <h2 className="ps-band-title" id={titleId}>
          Needs attention
        </h2>
        <div
          className="ps-card ps-needs-attention ps-needs-attention--empty"
          aria-labelledby={titleId}
        >
          <div className="ps-needs-attention__header ps-needs-attention__header--empty">
            <span className="ps-needs-attention__check" aria-hidden="true">
              <CheckIcon />
            </span>
            <div>
              <div className="ps-needs-attention__title">All caught up</div>
              <div className="ps-needs-attention__subtitle">
                No overdue assessments, open tasks, or missing vaccinations
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="ps-band-title" id={titleId}>
        Needs attention
      </h2>
      <aside
        className="ps-card ps-needs-attention na-rail"
        aria-labelledby={titleId}
      >
        <HeaderBand attentionCount={attentionCount} />
        <div className="na-rail-body">
          <AttentionSection
            label="Assessments"
            qualifier={`${assessmentsCount} overdue`}
            viewAllTo={`/patients/${patientId}/assessments`}
            items={assessmentRows}
            getKey={(item) => item.assessmentId}
            renderRow={(item) => (
              <AssessmentRailRow item={item} patientId={patientId} navigate={navigate} />
            )}
            emptyText="No overdue assessments"
            variant="rail"
          />
          <AttentionSection
            label="Tasks"
            qualifier={`${tasksCount} open`}
            viewAllTo={`/patients/${patientId}/task/me`}
            items={taskRows}
            getKey={(task) => task.taskId}
            renderRow={(task) => <TaskRailRow task={task} />}
            emptyText="No open tasks"
            variant="rail"
          />
          <AttentionSection
            label="Vaccinations"
            qualifier={`${vaccinationsCount} missing`}
            items={vaccinationRows}
            getKey={(item) => item.vaccine}
            renderRow={(item) => <VaccinationRailRow item={item} />}
            emptyText="No missing vaccinations"
            variant="rail"
          />
        </div>
      </aside>

      <section
        className="ps-card ps-needs-attention na-compact"
        aria-labelledby={titleId}
      >
        <HeaderBand attentionCount={attentionCount} />
        <div className="na-compact-body">
          <AttentionSection
            label="Assessments"
            qualifier={`${assessmentsCount}`}
            viewAllTo={`/patients/${patientId}/assessments`}
            items={assessmentRows}
            getKey={(item) => item.assessmentId}
            renderRow={(item) => (
              <AssessmentCompactRow item={item} patientId={patientId} navigate={navigate} />
            )}
            emptyText="No overdue assessments"
            variant="compact"
          />
          <AttentionSection
            label="Tasks"
            qualifier={`${tasksCount}`}
            viewAllTo={`/patients/${patientId}/task/me`}
            items={taskRows}
            getKey={(task) => task.taskId}
            renderRow={(task) => <TaskCompactRow task={task} />}
            emptyText="No open tasks"
            variant="compact"
          />
          <AttentionSection
            label="Vaccinations"
            qualifier={`${vaccinationsCount}`}
            items={vaccinationRows}
            getKey={(item) => item.vaccine}
            renderRow={(item) => <VaccinationCompactRow item={item} />}
            emptyText="No missing vaccinations"
            variant="compact"
          />
        </div>
      </section>
    </div>
  );
};
